import React, { useState, useEffect } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Grid,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Box,
  CircularProgress
} from "@mui/material";
import { Visibility, VisibilityOff, PersonAddAlt1 } from "@mui/icons-material";
import { useFormik } from "formik";
import * as yup from "yup";
import { getCountryCodes } from "Utils/country";

//Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

//Services
import Customercare from "Services/Customercare";

const CustomercareCreate = (props) => {
  const { openDialog, handleClose, onSuccess, isEditMode, initialValues } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const [countries, setCountries] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      const codeList = getCountryCodes();
      setCountries(codeList);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      fullName: initialValues?.user_name || initialValues?.fullName || "",
      email: initialValues?.email || "",
      password: "",
      passConfirmation: "",
      country: initialValues?.country || "",
      countryCode: initialValues?.countryCode || "+91",
      mobileNumber: initialValues?.mobileNumber || "",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      fullName: yup.string().required("Full Name is required"),
      email: yup.string().email("Invalid email format").required("Email is required"),
      password: isEditMode? yup.string() : yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
      passConfirmation: isEditMode? yup.string() : yup.string().oneOf([yup.ref("password")], "Passwords must match").required("Confirm password is required"),
      country: yup.string().required("Country is required"),
      countryCode: yup.string().required("Country code is required"),
      mobileNumber: yup.string().matches(/^\d{7,15}$/, "Invalid mobile number").required("Mobile number is required"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      dispatch(showLoader("Saving..."));

      const payload = {
        fullName: values.fullName,
        email: values.email,
        country: values.country,
        countryCode: values.countryCode,
        mobileNumber: values.mobileNumber,
      };
      if(values.password) {
        payload.password = values.password;
        payload.confirmPassword = values.passConfirmation;
      }

     
      
      const onSuccessRes = (res) => {
  setLoading(false);
  dispatch(hideLoader());
  if (res?.data?.status) { // <-- FIX: status not success
    dispatch(showSnackbar({ message: res.data.message, variant: "success", anchorOrigin: { vertical: "top", horizontal: "right" } }));
    formik.resetForm();
    handleClose(); // close modal
    onSuccess && onSuccess(); // refresh parent
  } else {
    dispatch(showSnackbar({ message: res.data.message, variant: "error" }));
  }
};
      
      
      const onFailure = (err) => {
        setLoading(false);
        dispatch(hideLoader());
        const msg = err?.response?.data?.message || "Error while saving";
        dispatch(showSnackbar({ message: msg, variant: "error" }));
      };

      if (isEditMode && initialValues?._id) {
        Customercare.updateCustomercare(initialValues._id, payload).then(onSuccessRes, onFailure);
      } else {
        Customercare.addCustomercare(payload).then(onSuccessRes, onFailure);
      }
    },
  });

  return (
    <Dialog
      open={openDialog}
      onClose={() => { formik.resetForm(); handleClose(); }}
      classes={{ paper: classes.dialogPaper }}
      maxWidth="md"
      fullWidth
    >
      {/* Custom Header matching precisely the design in image 2 */}
      <Box className={classes.headerContainer}>
        <Typography className={classes.dialogTitle}>Customer Care</Typography>
        <Typography className={classes.breadcrumb}>
          Admin / <span>Customer Care</span>
        </Typography>
      </Box>

      <DialogContent style={{ padding: 0 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Full Name */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Full Name</Typography>
              <TextField
                name="fullName"
                placeholder="e.g. Sarah Jenkins"
                value={formik.values.fullName}
                onChange={formik.handleChange}
                error={formik.touched.fullName && Boolean(formik.errors.fullName)}
                helperText={formik.touched.fullName && formik.errors.fullName}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
              />
            </Grid>

            {/* Email Address */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Email Address</Typography>
              <TextField
                name="email"
                placeholder="sarah.j@taxiflow.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
                disabled={isEditMode}
              />
            </Grid>

            {/* Password */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Password {isEditMode && <span style={{fontSize: 12, fontWeight: 400}}>(Leave blank to keep same)</span>}</Typography>
              <TextField
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="��������"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Password Confirmation */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Password Confirmation</Typography>
              <TextField
                name="passConfirmation"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="����"
                value={formik.values.passConfirmation}
                onChange={formik.handleChange}
                error={formik.touched.passConfirmation && Boolean(formik.errors.passConfirmation)}
                helperText={formik.touched.passConfirmation && formik.errors.passConfirmation}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Country */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Country</Typography>
              <TextField
                select
                name="country"
                value={formik.values.country}
                onChange={(e) => {
                  formik.handleChange(e);
                  const selectedCountry = countries.find(c => c.name === e.target.value);
                  if (selectedCountry) {
                    formik.setFieldValue("countryCode", selectedCountry.dial_code);
                  }
                }}
                error={formik.touched.country && Boolean(formik.errors.country)}
                helperText={formik.touched.country && formik.errors.country}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => value || <span style={{ color: "#94a3b8" }}>Select Country</span>
                }}
              >
                <MenuItem value="" disabled>Select Country</MenuItem>
                {countries.map((c) => <MenuItem key={c.code} value={c.name}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>

            {/* Mobile Number */}



            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Mobile Number</Typography>
              <Box className={classes.mobileInputWrapper}>
              


                    <TextField
  select
  name="countryCode"
  value={formik.values.countryCode}
  onChange={formik.handleChange}
  variant="outlined"
  size="small"
  sx={{
      width: 120,
      flexShrink: 0,
      "& .MuiSelect-select": {
        textOverflow: "clip",
        overflow: "visible",
        whiteSpace: "nowrap",
      },
    }}
  className={classes.formInput}
>
  {countries.map((c) => (
    <MenuItem key={c.code} value={c.dial_code}>
      {c.dial_code}
    </MenuItem>
  ))}
</TextField>


                <TextField
                  name="mobileNumber"
                  placeholder="000-000-0000"
                  value={formik.values.mobileNumber}
                  onChange={formik.handleChange}
                  error={formik.touched.mobileNumber && Boolean(formik.errors.mobileNumber)}
                  helperText={formik.touched.mobileNumber && formik.errors.mobileNumber}
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.formInput}
                />
              </Box>
            </Grid>

         



          </Grid>

          {/* Dialog Actions precisely styled as image 2 */}
          <Box className={classes.dialogActions}>
            <Button
              className={classes.cancelButton}
              onClick={() => { formik.resetForm(); handleClose(); }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              className={classes.submitButton}
              disabled={loading}
            >
              {loading ? <CircularProgress size={18} color="inherit" /> : <PersonAddAlt1 style={{ fontSize: "18px" }} />}
              {isEditMode ? "Save Changes" : "Add Customer Care"}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CustomercareCreate;